// @flow

import React from 'react';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import { Cookies, withCookies } from 'react-cookie';

import type { Comparable, ComparableKpi, KPIs, StoreGemini } from '../../../../../types/geminiType';
import { getShoppingCenter, getShoppingCenterNameByPcCode } from '../../../../../utils/urlUtils';
import { roundValue } from '../../../../../utils/formatUtils';
import CurrencySurfaceConvertor from '../../../../common/CurrencySurfaceConvertor';
import SurfaceConvertor from '../../../../common/SurfaceConvertor';
import {
  erv,
  glaDen,
  glaWithSst,
  glaWithoutSst,
  invSbr,
  occupancyCost,
  ocr,
  revPot,
  salesNum,
  salesR12m,
  spotMgr,
  salesR12mWithoutSst,
} from '../geminiConst';
import { computeOcupancyCost } from '../geminiFunctions';
import DraggableList, { DragHandle } from '../../../../../components/DraggableList';
import { twCurrency } from '../../../../../const';
import computeAvg from '../../../../../utils/computeAvg';
import EllipsisLoader from '../../../../../components/EllipsisLoader';

type Props = {
  storeGemini: StoreGemini,
  isCurrencyBySurface: boolean,
  cookies: Cookies,
  selectedCat: string,
  selectedCountryCodeList: string[],
  customComparables: ComparableKpi[],
  removeManuallyShoppingCenter: (number) => void,
  comparable: ?Comparable,
}

type State = {
  filteredStores: number[],
}

class Comparables extends React.Component<Props, State> {
  state: State = {
    filteredStores: [],
  };

  hideStore = (codstr: number) => {
    if (this.state.filteredStores.filter(cod => cod === codstr).length > 0) {
      this.setState({ filteredStores: this.state.filteredStores.filter(cod => cod !== codstr) });
    } else {
      this.setState({ filteredStores: this.state.filteredStores.concat(codstr) });
    }
  };

  compareKpi = (kpi1: KPIs, kpi2: KPIs, kpiParam: string): boolean => {
    if (kpiParam === salesR12m && this.props.isCurrencyBySurface) {
      if (!kpi2.glaDen) {
        return true;
      }
      return kpi1.salesNum / kpi1.glaDen > kpi2.salesNum / kpi2.glaDen;
    }
    if (kpiParam === occupancyCost) {
      if (this.props.isCurrencyBySurface) {
        return computeOcupancyCost(kpi1) / kpi1.glaWithSst >
          computeOcupancyCost(kpi2) / kpi2.glaWithSst;
      }
      return computeOcupancyCost(kpi1) > computeOcupancyCost(kpi2);
    }
    if (this.props.isCurrencyBySurface
      && kpiParam !== glaWithSst && kpiParam !== glaDen && kpiParam !== ocr) {
      return kpi1[kpiParam] / kpi1.glaWithSst > kpi2[kpiParam] / kpi2.glaWithSst;
    }
    return kpi1[kpiParam] > kpi2[kpiParam];
  };

  compareKpiAvg = (kpi1: KPIs, kpi2: { value: number, gla: number }, kpiParam: string): boolean => {
    if (kpiParam === salesR12m && this.props.isCurrencyBySurface) {
      return kpi1.salesNum / kpi1.glaDen > kpi2.value / kpi2.gla;
    }
    if (kpiParam === occupancyCost) {
      if (this.props.isCurrencyBySurface) {
        return computeOcupancyCost(kpi1) / kpi1.glaWithSst >
          kpi2.value / kpi2.gla;
      }
      return computeOcupancyCost(kpi1) > kpi2.value;
    }
    if (
      this.props.isCurrencyBySurface
      && kpiParam !== glaWithSst
      && kpiParam !== glaDen
      && kpiParam !== ocr
    ) {
      return kpi1[kpiParam] / kpi1.glaWithSst > kpi2.value / kpi2.gla;
    }
    return kpi1[kpiParam] > kpi2.value;
  };

  renderDraggableElement = (kpi, isAddedManually) => (
    <DragHandle
      key={kpi.pcCode}
      value={
        <div className={`comparable-column ${isAddedManually ? 'comparable-column--manually' : ''}`}>
          <div className="row-data comparable-title">
            <span>
              {kpi.comparableSC || getShoppingCenterNameByPcCode(kpi.pcCode)}
            </span>
            <span>-</span>
            <span>{kpi.libcom || kpi.comparableName}</span>
          </div>
          <div className="row-data">
            {
              isAddedManually
                ? (
                  <button
                    title={I18n.t('COMPARABLE.removeCustomStore')}
                    className="fas fa-trash"
                    onClick={() => this.props.removeManuallyShoppingCenter(kpi.codstr)}
                  />
                )
                : (
                  <button
                    className={this.state.filteredStores.filter(codstr => codstr === kpi.codstr).length > 0 ? 'fa fa-eye-slash' : 'fa fa-eye'}
                    onClick={() => this.hideStore(kpi.codstr)}
                  />
                )
            }
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.salesR12m && kpi.salesR12m && (this.compareKpi(this.props.storeGemini.kpi, kpi, salesR12m) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={kpi.salesR12m}
              gla={kpi.glaDen}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.salesR12m && kpi.salesR12m && (this.compareKpi(this.props.storeGemini.kpi, kpi, salesR12mWithoutSst) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={kpi.salesR12m}
              gla={kpi.glaWithoutSst}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.glaWithSst && kpi.glaWithSst && (this.compareKpi(this.props.storeGemini.kpi, kpi, glaWithSst) ? 'red' : 'green')}`}
          >
            <SurfaceConvertor
              value={kpi.glaWithSst}
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.glaWithoutSst && kpi.glaWithoutSst && (this.compareKpi(this.props.storeGemini.kpi, kpi, glaWithoutSst) ? 'red' : 'green')}`}
          >
            <SurfaceConvertor
              value={kpi.glaWithoutSst}
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.ocr && kpi.ocr && (this.props.storeGemini.kpi.ocr < kpi.ocr ? 'red' : 'green')}`}
          >
            {roundValue(kpi.ocr, this.props.cookies.get(twCurrency))}%
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.spotMgr && kpi.spotMgr && (this.compareKpi(this.props.storeGemini.kpi, kpi, spotMgr) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={kpi.spotMgr}
              gla={kpi.glaWithSst}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.erv && kpi.erv && (this.compareKpi(this.props.storeGemini.kpi, kpi, erv) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={kpi.erv}
              gla={kpi.glaWithSst}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.revPot && kpi.revPot && (this.compareKpi(this.props.storeGemini.kpi, kpi, revPot) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={kpi.revPot}
              gla={kpi.glaWithSst}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.invSbr && kpi.invSbr && (this.compareKpi(this.props.storeGemini.kpi, kpi, invSbr) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={kpi.invSbr}
              gla={kpi.glaWithSst}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.compareKpi(this.props.storeGemini.kpi, kpi, occupancyCost) ? 'red' : 'green'}`}
          >
            <CurrencySurfaceConvertor
              value={computeOcupancyCost(kpi)}
              gla={kpi.glaWithSst}
              withSymbol
            />
          </div>
        </div>
      }
    />
  )

  render() {
    if (!this.props.comparable || !this.props.storeGemini || !this.props.storeGemini.kpi) {
      return (<EllipsisLoader />);
    }
    let array: ComparableKpi[];
    if (this.props.selectedCat === 'category') {
      array = this.props.comparable.storeComparables
        .filter(comparable => comparable.idCategorie === this.props.comparable.idCategorie);
    } else if (this.props.selectedCat === 'country') {
      array = this.props.comparable.storeComparables
        .filter(comparable => comparable.countryCode === this.props.comparable.countryCode);
    } else {
      array = this.props.comparable.storeComparables;
    }

    array = array.sort((a: ComparableKpi, b: ComparableKpi) => getShoppingCenterNameByPcCode(a.pcCode).localeCompare(getShoppingCenterNameByPcCode(b.pcCode)));

    const avgGla = computeAvg(array, glaWithSst, this.state.filteredStores);
    const avgGlaWithoutSst = computeAvg(array, glaWithoutSst, this.state.filteredStores);
    const ocrAvg = computeAvg(array, ocr, this.state.filteredStores);
    const salesAvg = computeAvg(array, salesR12m, this.state.filteredStores);
    const salesWithoutSstAvg = computeAvg(array, salesR12m, this.state.filteredStores, true);
    const salesNumAvg = computeAvg(array, salesNum, this.state.filteredStores);
    const ervAvg = computeAvg(array, erv, this.state.filteredStores);
    const mgrAvg = computeAvg(array, spotMgr, this.state.filteredStores);
    const revPotAvg = computeAvg(array, revPot, this.state.filteredStores);
    const sbrAvg = computeAvg(array, invSbr, this.state.filteredStores);
    const occCostAvg = computeAvg(array, occupancyCost, this.state.filteredStores);

    return (
      <div className="gemini-comparable-container">
        <div className="average-column">
          <div className="row-data comparable-title" />
          <div className="row-data">
            <div>
              <Translate value="COMPARABLE.average" />
            </div>
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.salesR12m && salesAvg.value && (this.compareKpiAvg(this.props.storeGemini.kpi, this.props.isCurrencyBySurface ? salesNumAvg : salesAvg, salesR12m) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={this.props.isCurrencyBySurface ? salesNumAvg.value : salesAvg.value}
              gla={salesAvg.gla}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.salesR12m && this.props.storeGemini.kpi.glaWithoutSst && salesAvg.value && (this.compareKpiAvg(this.props.storeGemini.kpi, this.props.isCurrencyBySurface ? salesNumAvg : salesWithoutSstAvg, salesR12mWithoutSst) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={this.props.isCurrencyBySurface ? salesNumAvg.value : salesWithoutSstAvg.value}
              gla={salesWithoutSstAvg.gla}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.glaWithSst && avgGla.value && (this.compareKpiAvg(this.props.storeGemini.kpi, avgGla, glaWithSst) ? 'red' : 'green')}`}
          >
            <SurfaceConvertor
              value={avgGla.value}
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.glaWithoutSst && avgGlaWithoutSst.value && (this.compareKpiAvg(this.props.storeGemini.kpi, avgGlaWithoutSst, glaWithoutSst) ? 'red' : 'green')}`}
          >
            <SurfaceConvertor
              value={avgGlaWithoutSst.value}
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.ocr && ocrAvg.value && (this.compareKpiAvg(this.props.storeGemini.kpi, ocrAvg, ocr) ? 'green' : 'red')}`}
          >
            <div>{roundValue(ocrAvg.value, this.props.cookies.get(twCurrency))} %</div>
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.spotMgr && mgrAvg.value && (this.compareKpiAvg(this.props.storeGemini.kpi, mgrAvg, spotMgr) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={mgrAvg.value}
              gla={mgrAvg.gla}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.erv && ervAvg.value && (this.compareKpiAvg(this.props.storeGemini.kpi, ervAvg, erv) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={ervAvg.value}
              gla={ervAvg.gla}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.revPot && revPotAvg.value && (this.compareKpiAvg(this.props.storeGemini.kpi, revPotAvg, revPot) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={revPotAvg.value}
              gla={revPotAvg.gla}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${this.props.storeGemini.kpi.invSbr && sbrAvg.value && (this.compareKpiAvg(this.props.storeGemini.kpi, sbrAvg, invSbr) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={sbrAvg.value}
              gla={sbrAvg.gla}
              withSymbol
            />
          </div>
          <div
            className={`row-data ${(this.compareKpiAvg(this.props.storeGemini.kpi, occCostAvg, occupancyCost) ? 'red' : 'green')}`}
          >
            <CurrencySurfaceConvertor
              value={occCostAvg.value}
              gla={occCostAvg.gla}
              withSymbol
            />
          </div>
        </div>
        <div className="data-container">
          { this.props.customComparables.length === 0 &&
            array.filter(kpi => !this.state.filteredStores.includes(kpi.codstr)).length === 0 &&
            <div className="empty-state"><Translate value="SIDE_DATA.no_data" /></div>
          }
          <DraggableList
            className="data-content"
            list={
              this.props.customComparables.map(kpi => this.renderDraggableElement(kpi, true))
            }
          />
          <DraggableList
            className="data-content"
            list={
              array.filter(kpi => !this.state.filteredStores.includes(kpi.codstr)
                && (this.props.selectedCountryCodeList.length === 0 || this.props.selectedCountryCodeList.includes(kpi.countryCode)))
                .map(kpi => this.renderDraggableElement(kpi, false))
            }
          />
          <div className="data-content">
            {
              array.filter(kpi => this.state.filteredStores.includes(kpi.codstr)
                && (this.props.selectedCountryCodeList.length === 0 || this.props.selectedCountryCodeList.includes(kpi.countryCode)))
                .map(kpi => (
                  <div
                    key={kpi.pcCode}
                    className="comparable-column hide-store"
                  >
                    <div className="row-data comparable-title">
                      <span>
                        {getShoppingCenterNameByPcCode(kpi.pcCode)}
                      </span>
                      <span>-</span>
                      <span>{kpi.libcom}</span>
                    </div>
                    <div className="row-data">
                      <button
                        className={this.state.filteredStores.filter(codstr => codstr === kpi.codstr).length > 0 ? 'fa fa-eye-slash' : 'fa fa-eye'}
                        onClick={() => this.hideStore(kpi.codstr)}
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <CurrencySurfaceConvertor
                        value={kpi.salesR12m}
                        gla={kpi.glaDen}
                        withSymbol
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <CurrencySurfaceConvertor
                        value={kpi.salesR12m}
                        gla={kpi.glaWithoutSst}
                        withSymbol
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <SurfaceConvertor
                        value={kpi.glaWithSst}
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <SurfaceConvertor
                        value={kpi.glaWithoutSst}
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      {roundValue(kpi.ocr, this.props.cookies.get(twCurrency))}
                    </div>
                    <div
                      className="row-data"
                    >
                      <CurrencySurfaceConvertor
                        value={kpi.erv}
                        gla={kpi.glaWithSst}
                        withSymbol
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <CurrencySurfaceConvertor
                        value={kpi.revPot}
                        gla={kpi.glaWithSst}
                        withSymbol
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <CurrencySurfaceConvertor
                        value={kpi.spotMgr}
                        gla={kpi.glaWithSst}
                        withSymbol
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <CurrencySurfaceConvertor
                        value={kpi.invSbr}
                        gla={kpi.glaWithSst}
                        withSymbol
                      />
                    </div>
                    <div
                      className="row-data"
                    >
                      <CurrencySurfaceConvertor
                        value={computeOcupancyCost(kpi)}
                        gla={kpi.glaWithSst}
                        withSymbol
                      />
                    </div>
                  </div>
                ))
            }
          </div>
        </div>
      </div>
    );
  }
}

export default withCookies(connect(state => ({
  isCurrencyBySurface: state.store.isCurrencyBySurface,
}))(Comparables));
